import { useCallback, useEffect, useMemo, useState } from 'react';

import { INBOX_FEATURES_LIMIT } from '../utils/constants';

interface UseInboxPaginationProps<T> {
  items: T[];
  itemsPerPage?: number;
}

interface UseInboxPaginationReturn<T> {
  currentPage: number;
  totalPages: number;
  paginatedItems: T[];
  onPageChange: (page: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  resetPagination: () => void;
}

export function useInboxPagination<T>({
  items,
  itemsPerPage = INBOX_FEATURES_LIMIT,
}: UseInboxPaginationProps<T>): UseInboxPaginationReturn<T> {
  const [currentPage, setCurrentPage] = useState(1);

  const safeItems = useMemo(() => items ?? [], [items]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(safeItems.length / itemsPerPage));
  }, [safeItems.length, itemsPerPage]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return safeItems.slice(startIndex, endIndex);
  }, [safeItems, currentPage, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const onPageChange = useCallback(
    (page: number) => {
      const validPage = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(validPage);
    },
    [totalPages],
  );

  const onPreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  const onNextPage = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, [totalPages]);

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage,
    totalPages,
    paginatedItems,
    onPageChange,
    onPreviousPage,
    onNextPage,
    resetPagination,
  };
}
